import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Asterisk, ChevronUp, ChevronDown, Trash2, Plus, Download } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "yt-maker:scene-description-draft:v1";

const FIELD_DEFS = [
  { key: "scene_description", label: "Scene Description", required: true, rows: 4,
    placeholder: "What happens in this scene? Setting, characters, action…" },
  { key: "dialog", label: "Dialog", required: false, rows: 3,
    placeholder: 'e.g. Bella says: "let\'s go camping!"  Benny laughs and says: "i can\'t wait!"' },
  { key: "audio", label: "Audio", required: false, rows: 2,
    placeholder: "Music, ambient SFX, sound design notes…" },
  { key: "camera", label: "Camera", required: false, rows: 2,
    placeholder: "e.g. slow push-in on Bella's face, then pan right" },
  { key: "lighting", label: "Lighting", required: false, rows: 2,
    placeholder: "e.g. warm golden-hour, soft fill, fairy lights on tent" },
];

const emptyScene = (sceneNumber = "") => ({
  scene_number: sceneNumber ? String(sceneNumber) : "",
  scene_description: "",
  dialog: "",
  audio: "",
  camera: "",
  lighting: "",
});

function loadDraft() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.scenes)) return parsed;
  } catch { /* ignore */ }
  return null;
}

function saveDraft(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch { /* quota — ignore */ }
}

/**
 * Scene Description Creation — template-based authoring tool that
 * lets a creator define every scene of a video (channel + episode +
 * video name + N scenes with description / dialog / audio / camera /
 * lighting), then download the whole thing as TXT / DOCX / PDF.
 *
 * Pure authoring tool — does NOT generate videos.
 */
export default function SceneDescriptionCreate() {
  // Lazy initializers hydrate from localStorage on the FIRST render
  // so the auto-save effect never observes an empty closure. A naïve
  // restore-via-useEffect raced the auto-save effect and clobbered
  // the persisted draft (testing agent iteration_16). See LS_KEY.
  const draft = loadDraft();
  const [channelName, setChannelName] = useState(() => draft?.channel_name || "");
  const [episodeNum, setEpisodeNum] = useState(() => draft?.episode_num || "");
  const [videoName, setVideoName] = useState(() => draft?.video_name || "");
  const [language, setLanguage] = useState(() => draft?.language || "English");
  const [scenes, setScenes] = useState(() =>
    Array.isArray(draft?.scenes) && draft.scenes.length
      ? draft.scenes
      : [emptyScene("1")],
  );
  const [downloading, setDownloading] = useState("");

  // Auto-save draft on every change.
  useEffect(() => {
    saveDraft({
      channel_name: channelName,
      episode_num: episodeNum,
      video_name: videoName,
      language,
      scenes,
    });
  }, [channelName, episodeNum, videoName, language, scenes]);

  const updateScene = (idx, key, value) => {
    setScenes((arr) => arr.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };

  const moveScene = (idx, dir) => {
    setScenes((arr) => {
      const next = [...arr];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return arr;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeScene = (idx) => {
    setScenes((arr) => (arr.length === 1 ? [emptyScene("1")] : arr.filter((_, i) => i !== idx)));
  };

  /**
   * Refs to every per-scene `Scene Description` textarea so the SAVE
   * button can focus the *new* scene's first field after appending.
   * Keyed by scene index; React replaces the ref on each render so
   * the latest DOM node is always reachable.
   */
  const sceneDescRefs = useRef({});
  const registerSceneRef = (idx, node) => {
    if (node) sceneDescRefs.current[idx] = node;
    else delete sceneDescRefs.current[idx];
  };

  /**
   * Commits the current (last) scene by appending a new empty one
   * beneath it, then focuses + scrolls the new scene's first field
   * into view so the user can keep typing without lifting their
   * hands off the keyboard. The previously-typed scene stays in the
   * scenes array (and on disk via auto-save) so it's preserved in
   * the master doc.
   */
  const saveCurrentScene = () => {
    const lastIdx = scenes.length - 1;
    const lastScene = scenes[lastIdx];
    if (!lastScene || !(lastScene.scene_description || "").trim()) {
      toast.error("Fill in the Scene Description before saving");
      return;
    }
    const nextIdx = scenes.length;
    // Default the new scene's number to one-past-the-last user-entered
    // numeric value when possible; fall back to sequential index.
    const lastNum = parseInt(lastScene.scene_number || "", 10);
    const nextSceneNumber = Number.isFinite(lastNum)
      ? String(lastNum + 1)
      : String(nextIdx + 1);
    setScenes((arr) => [...arr, emptyScene(nextSceneNumber)]);
    toast.success(`Saved scene ${lastScene.scene_number || lastIdx + 1} — start scene ${nextSceneNumber}`);
    // Wait one frame so the new textarea actually exists in the DOM.
    setTimeout(() => {
      const node = sceneDescRefs.current[nextIdx];
      if (!node) return;
      node.focus({ preventScroll: true });
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const clearAll = () => {
    if (!window.confirm("Clear the entire form? This wipes your local draft.")) return;
    setChannelName("");
    setEpisodeNum("");
    setVideoName("");
    setLanguage("English");
    setScenes([emptyScene("1")]);
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    toast.success("Cleared");
  };

  const validate = () => {
    if (!channelName.trim()) return "Channel Name is required";
    if (!videoName.trim()) return "Video Name is required";
    const validScenes = scenes.filter((s) => (s.scene_description || "").trim());
    if (validScenes.length === 0) return "At least one scene needs a Scene Description";
    return null;
  };

  const download = async (fmt) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setDownloading(fmt);
    try {
      const res = await fetch(`${API_BASE}/api/scene-descriptions/download`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel_name: channelName,
          episode_num: episodeNum,
          video_name: videoName,
          language,
          scenes,
          format: fmt,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `scenes.${fmt === "docx" ? "docx" : fmt}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading("");
    }
  };

  return (
    <main
      data-testid="scene-description-page"
      className="px-6 py-8 max-w-5xl mx-auto space-y-8"
    >
      <header className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
          // SCENE DESCRIPTION CREATION
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">
          Author every scene of your video
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Fill out a structured template for each scene — description,
          dialog, audio, camera, lighting — then download the master
          document as TXT, DOCX, or PDF for hand-off to your animator
          or AI video pipeline. Fields marked with{" "}
          <Asterisk className="inline w-3 h-3 text-yellow-400" /> are required.
        </p>
      </header>

      {/* Header (channel + episode + video) */}
      <section
        data-testid="scenedesc-header-form"
        className="border border-zinc-800 rounded-sm p-5 bg-black space-y-4"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
          // VIDEO META
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FieldInput
            testId="scenedesc-channel"
            label="Channel Name"
            required
            value={channelName}
            onChange={setChannelName}
            placeholder="e.g. Bella's Play Club"
          />
          <FieldInput
            testId="scenedesc-episode"
            label="Episode #"
            value={episodeNum}
            onChange={setEpisodeNum}
            placeholder="e.g. 12"
          />
          <FieldInput
            testId="scenedesc-video"
            label="Video Name"
            required
            value={videoName}
            onChange={setVideoName}
            placeholder="e.g. Bella Goes Camping"
          />
          <FieldSelect
            testId="scenedesc-language"
            label="Language"
            value={language}
            onChange={setLanguage}
            options={["English", "Spanish"]}
          />
        </div>
      </section>

      {/* Scenes */}
      <section className="space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
            // SCENES ({scenes.length})
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Add as many scenes as you need. Re-order with the arrows. Use{" "}
            <span className="text-yellow-300">+ Add scene</span> below the last
            scene to keep authoring.
          </p>
        </div>
        {scenes.map((scene, idx) => (
          <SceneCard
            key={idx}
            idx={idx}
            scene={scene}
            isFirst={idx === 0}
            isLast={idx === scenes.length - 1}
            onChange={(key, value) => updateScene(idx, key, value)}
            onMoveUp={() => moveScene(idx, -1)}
            onMoveDown={() => moveScene(idx, 1)}
            onRemove={() => removeScene(idx)}
            sceneDescRef={(node) => registerSceneRef(idx, node)}
          />
        ))}
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="scenedesc-save-scene"
            onClick={saveCurrentScene}
            className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-black px-6 py-3 rounded-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Save
          </button>
        </div>
      </section>

      {/* Download bar */}
      <section
        data-testid="scenedesc-download-bar"
        className="border border-emerald-400/40 bg-emerald-400/5 rounded-sm p-5 flex flex-wrap items-center gap-3"
      >
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold">
            // EXPORT MASTER DOC
          </div>
          <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
            One file containing every scene above, formatted and labelled.
            Pick a format below — your work is auto-saved locally either way.
          </div>
        </div>
        <DownloadBtn fmt="txt" label="TXT" downloading={downloading} onClick={() => download("txt")} />
        <DownloadBtn fmt="docx" label="DOCX" downloading={downloading} onClick={() => download("docx")} />
        <DownloadBtn fmt="pdf" label="PDF" downloading={downloading} onClick={() => download("pdf")} />
        <button
          type="button"
          data-testid="scenedesc-clear"
          onClick={clearAll}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400 hover:text-red-300 border border-red-500/40 hover:border-red-500/70 px-3 py-2 rounded-sm transition-colors"
        >
          ✕ Clear all
        </button>
      </section>
    </main>
  );
}

function FieldInput({ label, required, value, onChange, placeholder, testId }) {
  return (
    <label className="block space-y-1.5">
      <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 flex items-center gap-1">
        {label}
        {required && <Asterisk className="w-3.5 h-3.5 text-yellow-400" />}
      </span>
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100"
      />
    </label>
  );
}

function FieldSelect({ label, required, value, onChange, options, testId }) {
  return (
    <label className="block space-y-1.5">
      <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 flex items-center gap-1">
        {label}
        {required && <Asterisk className="w-3.5 h-3.5 text-yellow-400" />}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100 cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-black text-zinc-100">
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function FieldTextarea({ label, required, value, onChange, placeholder, rows = 3, testId, innerRef }) {
  return (
    <label className="block space-y-1.5">
      <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 flex items-center gap-1">
        {label}
        {required && <Asterisk className="w-3.5 h-3.5 text-yellow-400" />}
      </span>
      <textarea
        ref={innerRef}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100 leading-relaxed resize-y"
      />
    </label>
  );
}

function SceneCard({ idx, scene, isFirst, isLast, onChange, onMoveUp, onMoveDown, onRemove, sceneDescRef }) {
  const headerLabel = (scene.scene_number || "").trim() || String(idx + 1);
  return (
    <article
      data-testid={`scenedesc-card-${idx}`}
      className="border border-zinc-800 rounded-sm bg-black p-5 space-y-4"
    >
      <div className="flex items-center justify-between gap-3 border-b border-zinc-900 pb-3">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold">
          // SCENE {headerLabel}
        </div>
        <div className="flex items-center gap-1">
          <IconBtn
            disabled={isFirst}
            onClick={onMoveUp}
            label="Move scene up"
            testId={`scenedesc-up-${idx}`}
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn
            disabled={isLast}
            onClick={onMoveDown}
            label="Move scene down"
            testId={`scenedesc-down-${idx}`}
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn
            onClick={onRemove}
            label="Remove scene"
            danger
            testId={`scenedesc-remove-${idx}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </IconBtn>
        </div>
      </div>
      <div className="max-w-[180px]">
        <FieldInput
          testId={`scenedesc-scene-number-${idx}`}
          label="Scene #"
          value={scene.scene_number || ""}
          onChange={(v) => onChange("scene_number", v)}
          placeholder="e.g. 1, 2a, intro"
        />
      </div>
      {FIELD_DEFS.map((f) => (
        <FieldTextarea
          key={f.key}
          testId={`scenedesc-${f.key}-${idx}`}
          label={f.label}
          required={f.required}
          value={scene[f.key]}
          onChange={(v) => onChange(f.key, v)}
          rows={f.rows}
          placeholder={f.placeholder}
          innerRef={f.key === "scene_description" ? sceneDescRef : undefined}
        />
      ))}
    </article>
  );
}

function IconBtn({ children, onClick, disabled, label, danger, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`p-1.5 rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "text-red-400 hover:text-red-300 hover:bg-red-500/10"
          : "text-zinc-400 hover:text-yellow-400 hover:bg-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}

function DownloadBtn({ fmt, label, downloading, onClick }) {
  const isThis = downloading === fmt;
  return (
    <button
      type="button"
      data-testid={`scenedesc-download-${fmt}`}
      onClick={onClick}
      disabled={!!downloading}
      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400 hover:bg-emerald-300 text-black font-bold px-4 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <Download className="w-3 h-3" />
      {isThis ? `Building ${label}…` : `Download ${label}`}
    </button>
  );
}
